import React, { useState, useEffect } from "react";
import {
    TextField,
    Button,
    Card,
    CardContent,
    Typography,
    List,
    ListItem,
    IconButton,
    FormHelperText,
    FormGroup,
    FormControlLabel,
    Checkbox,
    Box,
    Select,
    MenuItem,
    FormControl,
    InputLabel,
    Divider,
    Radio,
    RadioGroup
} from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";
import { searchTrainers } from "../api/affiliateApi";

const PAYMENT_COUNTRY_OPTIONS = [
    { value: "EE", label: "Estonia" },
    { value: "LV", label: "Latvia" },
    { value: "LT", label: "Lithuania" },
    { value: "PL", label: "Poland" },
    { value: "FI", label: "Finland" },
];

const PAYMENT_CURRENCY_OPTIONS = [
    { value: "EUR", label: "EUR" },
    { value: "PLN", label: "PLN" },
];

export default function AffiliateEdit({ affiliate, trainers, onSave, onCancel }) {
    const [form, setForm] = useState({
        ...affiliate,
        paymentCountry: affiliate.paymentCountry || "EE",
        paymentCurrency: affiliate.paymentCurrency || "EUR",
        isMontonioBankPaymentEnabled: affiliate.isMontonioBankPaymentEnabled !== false,
        isMontonioCardPaymentEnabled: affiliate.isMontonioCardPaymentEnabled === true,
        isMontonioBlikPaymentEnabled: affiliate.paymentCurrency === "PLN" && affiliate.isMontonioBlikPaymentEnabled === true,
    });
    const [selectedTrainers, setSelectedTrainers] = useState([...trainers]);
    const [trainerSearch, setTrainerSearch] = useState("");
    const [searchResults, setSearchResults] = useState([]);
    const [sendPlanBuyEmail, setSendPlanBuyEmail] = useState(
        affiliate.sendPlanBuyEmail !== false // default true if undefined/null
    );
    const [isShopActive, setIsShopActive] = useState(
        affiliate.isShopActive === true
    );
    const [isClassListVisible, setIsClassListVisible] = useState(
        affiliate.isClassListVisible !== false // default true kui undefined/null
    );

    const [cancelBeforeHours, setCancelBeforeHours] = useState(
        affiliate.cancelBeforeHours !== null && affiliate.cancelBeforeHours !== undefined
            ? affiliate.cancelBeforeHours
            : 0 // default 1 hours
    );

    const [isAllClassListVisible, setIsAllClassListVisible] = useState(
        affiliate.isAllClassListVisible !== false
    );
    const [showTrainerNamesInSchedule, setShowTrainerNamesInSchedule] = useState(
        affiliate.showTrainerNamesInSchedule !== false
    );
    const [workoutInfoVisibilityMode, setWorkoutInfoVisibilityMode] = useState(
        affiliate.workoutInfoVisibilityMode || "always"
    );

    // Training options
    const trainingOptions = [
        'All classes',
        'WOD',
        'Weightlifting',
        'Rowing',
        'Gymnastics',
        'Open Gym',
        'Cardio',
        'Kids',
        'Basic',
        'Hyrox',
        'Circuit',
        'Strength',
        'Other'
    ];

    // Parse existing freeFirstTrainingList and set as initial state
    const [selectedTrainings, setSelectedTrainings] = useState(
        (() => {
            if (!affiliate.freeFirstTrainingList) return [];

            try {
                // If it's already an array, return it
                if (Array.isArray(affiliate.freeFirstTrainingList)) {
                    return affiliate.freeFirstTrainingList;
                }

                // If it's a string, try to parse it
                if (typeof affiliate.freeFirstTrainingList === 'string') {
                    return JSON.parse(affiliate.freeFirstTrainingList);
                }

                return [];
            } catch (error) {
                console.error('Error parsing freeFirstTrainingList:', error);
                return [];
            }
        })()
    );

    // Add registration period state with default value
    const [registrationPeriod, setRegistrationPeriod] = useState(
        affiliate.registrationPeriod || 1
    );

    // Add errors state to track validation errors
    const [errors, setErrors] = useState({
        name: false,
        address: false,
        trainingType: false,
        vatRate: false
    });

    const handleChange = (e) => {
        const { name, value } = e.target;

        // Handle VAT rate validation
        if (name === 'VATRate') {
            const numValue = parseFloat(value);
            if (value === '' || (!isNaN(numValue) && numValue >= 0 && numValue <= 100)) {
                setForm({ ...form, [name]: value === '' ? null : numValue });
                if (errors[name]) {
                    setErrors({ ...errors, [name]: false });
                }
            }
            return;
        }

        // Handle paymentHolidayFee - convert to number, must be null or >= 0
        if (name === 'paymentHolidayFee') {
            const numValue = parseFloat(value);
            if (value === '' || (!isNaN(numValue) && numValue >= 0)) {
                setForm({ ...form, [name]: value === '' ? null : numValue });
            }
            return;
        }

        // Handle minimumPaymentHolidayWeeks - convert to integer with min/max validation
        if (name === 'minimumPaymentHolidayWeeks') {
            const numValue = parseInt(value, 10);
            if (value === '') {
                setForm({ ...form, [name]: null });
            } else if (!isNaN(numValue) && numValue >= 1 && numValue <= 52) {
                setForm({ ...form, [name]: numValue });
            }
            return;
        }

        const nextForm = {
            ...form,
            [name]: value,
            ...(name === "paymentCurrency" && value !== "PLN" ? { isMontonioBlikPaymentEnabled: false } : {}),
        };

        if (
            name === "paymentCurrency"
            && value !== "PLN"
            && !nextForm.isMontonioBankPaymentEnabled
            && !nextForm.isMontonioCardPaymentEnabled
        ) {
            nextForm.isMontonioBankPaymentEnabled = true;
        }

        setForm(nextForm);

        // Clear error for this field when user types something
        if (errors[name]) {
            setErrors({ ...errors, [name]: false });
        }
    };

    // Handle registration period change
    const handleRegistrationPeriodChange = (event) => {
        setRegistrationPeriod(event.target.value);
    };

    const handleCancelBeforeHoursChange = (event) => {
        setCancelBeforeHours(event.target.value);
    };

    const handleMontonioPaymentMethodChange = (event) => {
        const { name, checked } = event.target;
        const nextForm = { ...form, [name]: checked };

        const isBlikAvailable = nextForm.paymentCurrency === "PLN";

        if (
            !nextForm.isMontonioBankPaymentEnabled
            && !nextForm.isMontonioCardPaymentEnabled
            && !(isBlikAvailable && nextForm.isMontonioBlikPaymentEnabled)
        ) {
            return;
        }

        setForm(nextForm);
    };

    // Handle training checkbox changes
    const handleTrainingChange = (trainingType) => {
        setSelectedTrainings(prev => {
            if (prev.includes(trainingType)) {
                return prev.filter(t => t !== trainingType);
            } else {
                return [...prev, trainingType];
            }
        });
    };

    const handleRemoveTrainer = (trainerId) => {
        setSelectedTrainers(selectedTrainers.filter((t) => t.trainerId !== trainerId));
    };

    const handleTrainerSearch = async (e) => {
        const query = e.target.value;
        setTrainerSearch(query);

        if (query.length > 2) {
            const results = await searchTrainers(query);
            setSearchResults(results);
        } else {
            setSearchResults([]);
        }
    };

    const handleSelectTrainer = (trainer) => {
        if (!selectedTrainers.some((t) => t.trainerId === trainer.id)) {
            setSelectedTrainers([...selectedTrainers, { trainerId: trainer.id, fullName: trainer.fullName, username: trainer.username }]);
        }
        setTrainerSearch("");
        setSearchResults([]);
    };

    // Function to validate the form
    const validateForm = () => {
        const newErrors = {
            name: !form.name,
            address: !form.address,
            trainingType: !form.trainingType,
            vatRate: form.VATRate !== null && form.VATRate !== undefined && (isNaN(form.VATRate) || form.VATRate < 0 || form.VATRate > 100)
        };

        setErrors(newErrors);

        // Form is valid if no errors (all required fields are filled)
        return !Object.values(newErrors).some(error => error);
    };

    const handleSave = () => {
        // Validate the form before submission
        const isValid = validateForm();

        if (isValid) {

            let websiteValue = form.website || '';

            // Kui väli pole tühi ja ei alga http:// või https:// eesliitega
            if (websiteValue && !websiteValue.match(/^https?:\/\//)) {
                websiteValue = 'https://' + websiteValue;
            }

            const updatedForm = {
                ...form,
                id: form.id || affiliate.id, // Kasuta ID-d, mis saadi `MyAffiliate.js` kaudu
                website: websiteValue,
                registrationPeriod: registrationPeriod, // Add registration period to form data
                freeFirstTrainingList: JSON.stringify(selectedTrainings), // Convert to JSON string for backend
                isClassListVisible: isClassListVisible,
                sendPlanBuyEmail: sendPlanBuyEmail,
                isShopActive: isShopActive,
                cancelBeforeHours: cancelBeforeHours,
                isAllClassListVisible: isAllClassListVisible,
                showTrainerNamesInSchedule,
                workoutInfoVisibilityMode,
                trainers: selectedTrainers.map(trainer => ({
                    trainerId: trainer.trainerId,
                    fullName: trainer.fullName,
                    username: trainer.username
                })) // ✅ Lisa treenerid `updatedForm` objekti
            };

            onSave(updatedForm);
        }
        // If not valid, the errors will be displayed and form won't submit
    };

    return (
        <Card sx={{ maxWidth: 600, margin: "auto", mt: 4, p: 3, bgcolor: "background.paper" }}>
            <CardContent>
                <Typography variant="h5" gutterBottom>Edit Affiliate</Typography>

                {/* Basic Information Section */}
                <Typography variant="h6" sx={{ mt: 3, mb: 2, color: 'primary.main' }}>
                    Basic Information
                </Typography>

                <TextField
                    label="Affiliate Name*"
                    name="name"
                    fullWidth
                    margin="normal"
                    value={form.name || ''}
                    onChange={handleChange}
                    error={errors.name}
                    helperText={errors.name ? "Affiliate name is required" : ""}
                />

                <TextField
                    label="Address*"
                    name="address"
                    fullWidth
                    margin="normal"
                    value={form.address || ''}
                    onChange={handleChange}
                    error={errors.address}
                    helperText={errors.address ? "Address is required" : ""}
                />

                <TextField
                    label="Training Type*"
                    name="trainingType"
                    fullWidth
                    margin="normal"
                    value={form.trainingType || ''}
                    onChange={handleChange}
                    error={errors.trainingType}
                    helperText={errors.trainingType ? "Training type is required" : ""}
                />

                <TextField
                    label="Email*"
                    name="email"
                    fullWidth
                    margin="normal"
                    value={form.email || ''}
                    onChange={handleChange}
                />

                <TextField
                    label="Website"
                    name="website"
                    fullWidth
                    margin="normal"
                    value={form.website || ''}
                    onChange={handleChange}
                />

                <TextField
                    label="Feedback link"
                    name="feedback"
                    fullWidth
                    margin="normal"
                    value={form.feedback || ''}
                    onChange={handleChange}
                />

                <TextField
                    label="Phone"
                    name="phone"
                    fullWidth
                    margin="normal"
                    value={form.phone || ''}
                    onChange={handleChange}
                />

                <TextField
                    label="Payment Holiday Fee"
                    name="paymentHolidayFee"
                    fullWidth
                    margin="normal"
                    value={form.paymentHolidayFee !== null && form.paymentHolidayFee !== undefined ? form.paymentHolidayFee : ''}
                    onChange={handleChange}
                    type="number"
                    inputProps={{ min: 0, step: 0.01 }}
                    helperText="Monthly fee charged during payment holiday periods (leave empty for null, or enter 0 or positive value)"
                />

                <TextField
                    label="Minimum Payment Holiday Period (weeks)"
                    name="minimumPaymentHolidayWeeks"
                    fullWidth
                    margin="normal"
                    value={form.minimumPaymentHolidayWeeks || 4}
                    onChange={handleChange}
                    type="number"
                    inputProps={{ min: 1, max: 52 }}
                    helperText={`Minimum duration for payment holiday requests (currently ${(form.minimumPaymentHolidayWeeks || 4) * 7} days)`}
                />

                <Divider sx={{ my: 3 }} />

                {/* Business Information Section */}
                <Typography variant="h6" sx={{ mb: 2, color: 'primary.main' }}>
                    Business Information
                </Typography>

                <TextField
                    label="Legal Name"
                    name="legalName"
                    fullWidth
                    margin="normal"
                    value={form.legalName || ''}
                    onChange={handleChange}
                    helperText="Official legal name of the company as registered"
                />

                <TextField
                    label="Registry Code"
                    name="registryCode"
                    fullWidth
                    margin="normal"
                    value={form.registryCode || ''}
                    onChange={handleChange}
                    helperText="Company registry code or business registration number"
                />

                <TextField
                    label="VAT Number"
                    name="VATNumber"
                    fullWidth
                    margin="normal"
                    value={form.VATNumber || ''}
                    onChange={handleChange}
                    helperText="Value Added Tax identification number"
                />

                <TextField
                    label="VAT Rate (%)"
                    name="VATRate"
                    fullWidth
                    margin="normal"
                    type="number"
                    value={form.VATRate !== null && form.VATRate !== undefined ? form.VATRate : ''}
                    onChange={handleChange}
                    error={errors.vatRate}
                    helperText={errors.vatRate ? "VAT rate must be between 0-100%" : "VAT rate percentage (e.g., 20 for 20%)"}
                    inputProps={{ min: 0, max: 100, step: 0.1 }}
                />

                <Divider sx={{ my: 3 }} />

                {/* Banking Information Section */}
                <Typography variant="h6" sx={{ mb: 2, color: 'primary.main' }}>
                    Banking Information
                </Typography>

                <TextField
                    label="IBAN"
                    name="iban"
                    fullWidth
                    margin="normal"
                    value={form.iban || ''}
                    onChange={handleChange}
                    helperText="International Bank Account Number for payments"
                />

                <TextField
                    label="Bank Name"
                    name="bankName"
                    fullWidth
                    margin="normal"
                    value={form.bankName || ''}
                    onChange={handleChange}
                    helperText="Name of the bank (e.g., Swedbank, SEB)"
                />

                <FormControl fullWidth margin="normal">
                    <InputLabel id="payment-country-label">Payment Country</InputLabel>
                    <Select
                        labelId="payment-country-label"
                        name="paymentCountry"
                        value={form.paymentCountry || "EE"}
                        label="Payment Country"
                        onChange={handleChange}
                    >
                        {PAYMENT_COUNTRY_OPTIONS.map((country) => (
                            <MenuItem key={country.value} value={country.value}>
                                {country.label}
                            </MenuItem>
                        ))}
                    </Select>
                    <FormHelperText>
                        Default country shown for Montonio bank payments.
                    </FormHelperText>
                </FormControl>

                <FormControl fullWidth margin="normal">
                    <InputLabel id="payment-currency-label">Payment Currency</InputLabel>
                    <Select
                        labelId="payment-currency-label"
                        name="paymentCurrency"
                        value={form.paymentCurrency || "EUR"}
                        label="Payment Currency"
                        onChange={handleChange}
                    >
                        {PAYMENT_CURRENCY_OPTIONS.map((currency) => (
                            <MenuItem key={currency.value} value={currency.value}>
                                {currency.label}
                            </MenuItem>
                        ))}
                    </Select>
                    <FormHelperText>
                        Currency used for Montonio payments.
                    </FormHelperText>
                </FormControl>

                <Box sx={{ mt: 2 }}>
                    <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                        Montonio Payment Methods
                    </Typography>
                    <FormGroup>
                        <FormControlLabel
                            control={
                                <Checkbox
                                    checked={form.isMontonioBankPaymentEnabled !== false}
                                    onChange={handleMontonioPaymentMethodChange}
                                    name="isMontonioBankPaymentEnabled"
                                />
                            }
                            label="Bank payment"
                        />
                        <FormControlLabel
                            control={
                                <Checkbox
                                    checked={form.isMontonioCardPaymentEnabled === true}
                                    onChange={handleMontonioPaymentMethodChange}
                                    name="isMontonioCardPaymentEnabled"
                                />
                            }
                            label="Card payment"
                        />
                        <FormControlLabel
                            control={
                                <Checkbox
                                    checked={form.paymentCurrency === "PLN" && form.isMontonioBlikPaymentEnabled === true}
                                    onChange={handleMontonioPaymentMethodChange}
                                    name="isMontonioBlikPaymentEnabled"
                                    disabled={form.paymentCurrency !== "PLN"}
                                />
                            }
                            label="BLIK"
                        />
                    </FormGroup>
                    <FormHelperText>
                        At least one Montonio payment method must stay enabled. BLIK is available only for PLN payments. MobilePay is shown automatically for Finland and EUR when card payments are enabled.
                    </FormHelperText>
                </Box>

                <Divider sx={{ my: 3 }} />

                {/* Registration Settings Section */}
                <Typography variant="h6" sx={{ mb: 2, color: 'primary.main' }}>
                    Registration Settings
                </Typography>

                <FormControl fullWidth margin="normal">
                    <InputLabel id="cancel-before-hours-label">Cancel Before Hours</InputLabel>
                    <Select
                        labelId="cancel-before-hours-label"
                        value={cancelBeforeHours}
                        label="Cancel Before Hours"
                        onChange={handleCancelBeforeHoursChange}
                    >
                        <MenuItem value={0}>0 hours</MenuItem>
                        <MenuItem value={1}>1 hour</MenuItem>
                        <MenuItem value={2}>2 hours</MenuItem>
                        <MenuItem value={3}>3 hours</MenuItem>
                        <MenuItem value={4}>4 hours</MenuItem>
                        <MenuItem value={5}>5 hours</MenuItem>
                        <MenuItem value={6}>6 hours</MenuItem>
                        <MenuItem value={7}>7 hours</MenuItem>
                        <MenuItem value={8}>8 hours</MenuItem>
                        <MenuItem value={9}>9 hours</MenuItem>
                        <MenuItem value={10}>10 hours</MenuItem>
                    </Select>
                    <FormHelperText>
                        How many hours before class start can users cancel their registration
                    </FormHelperText>
                </FormControl>

                {/* Registration Period Selection */}
                <FormControl fullWidth margin="normal">
                    <InputLabel id="registration-period-label">Registration Period</InputLabel>
                    <Select
                        labelId="registration-period-label"
                        value={registrationPeriod}
                        label="Registration Period"
                        onChange={handleRegistrationPeriodChange}
                    >
                        <MenuItem value={1}>1 week</MenuItem>
                        <MenuItem value={2}>2 weeks</MenuItem>
                        <MenuItem value={3}>3 weeks</MenuItem>
                        <MenuItem value={4}>4 weeks</MenuItem>
                        <MenuItem value={8}>8 weeks</MenuItem>
                        <MenuItem value={12}>12 weeks</MenuItem>
                    </Select>
                    <FormHelperText>
                        How many weeks in advance can users register for classes? Default is 1 week.
                    </FormHelperText>
                </FormControl>

                {/* Send Plan Buy Email Checkbox */}
                <FormControlLabel
                    control={
                        <Checkbox
                            checked={sendPlanBuyEmail}
                            onChange={(e) => setSendPlanBuyEmail(e.target.checked)}
                            name="sendPlanBuyEmail"
                        />
                    }
                    label="Send email notifications for plan purchases"
                    sx={{ mt: 2 }}
                />
                <FormHelperText sx={{ ml: 4, mt: 0 }}>
                    Receive email notifications when users purchase plans
                </FormHelperText>

                <FormControlLabel
                    control={
                        <Checkbox
                            checked={isShopActive}
                            onChange={(e) => setIsShopActive(e.target.checked)}
                            name="isShopActive"
                        />
                    }
                    label="Enable Shop for regular users"
                    sx={{ mt: 2 }}
                />
                <FormHelperText sx={{ ml: 4, mt: 0 }}>
                    If disabled, Shop button is hidden in Register Training view
                </FormHelperText>

                {/* Participant List Visibility Settings */}
                <Box sx={{ mt: 3 }}>
                    <Typography variant="h6" gutterBottom>
                        Class Participant List Visibility
                    </Typography>
                    <FormControl component="fieldset">
                        <RadioGroup
                            value={
                                isAllClassListVisible ? 'all' :
                                    isClassListVisible ? 'registered' :
                                        'none'
                            }
                            onChange={(e) => {
                                const value = e.target.value;
                                if (value === 'none') {
                                    setIsClassListVisible(false);
                                    setIsAllClassListVisible(false);
                                } else if (value === 'registered') {
                                    setIsClassListVisible(true);
                                    setIsAllClassListVisible(false);
                                } else if (value === 'all') {
                                    setIsClassListVisible(false);
                                    setIsAllClassListVisible(true);
                                }
                            }}
                        >
                            <FormControlLabel
                                value="none"
                                control={<Radio />}
                                label="Hidden - No one can see participant lists"
                            />
                            <FormControlLabel
                                value="registered"
                                control={<Radio />}
                                label="Registered Only - Only registered members can see who else is registered"
                            />
                            <FormControlLabel
                                value="all"
                                control={<Radio />}
                                label="Members with active plan - Only members with an active package at this club can see participant lists"
                            />
                        </RadioGroup>
                    </FormControl>
                </Box>

                <FormControlLabel
                    control={
                        <Checkbox
                            checked={showTrainerNamesInSchedule}
                            onChange={(e) => setShowTrainerNamesInSchedule(e.target.checked)}
                            name="showTrainerNamesInSchedule"
                        />
                    }
                    label="Show trainer names in the class schedule to regular users"
                    sx={{ mt: 2 }}
                />
                <FormHelperText sx={{ ml: 4, mt: 0 }}>
                    This setting applies only to this club
                </FormHelperText>

                <Box sx={{ mt: 3 }}>
                    <Typography variant="h6" gutterBottom>
                        Workout Info Visibility
                    </Typography>
                    <FormControl component="fieldset">
                        <RadioGroup
                            value={workoutInfoVisibilityMode}
                            onChange={(e) => setWorkoutInfoVisibilityMode(e.target.value)}
                        >
                            <FormControlLabel
                                value="always"
                                control={<Radio />}
                                label="Always - Users always see Workout Info"
                            />
                            <FormControlLabel
                                value="registered"
                                control={<Radio />}
                                label="Registered Only - Users see Workout Info only when registered"
                            />
                            <FormControlLabel
                                value="plan"
                                control={<Radio />}
                                label="Plan Required - Users see Workout Info only with a compatible class-type plan"
                            />
                        </RadioGroup>
                    </FormControl>
                </Box>

                {/* Free First Training Selection */}
                <Box sx={{ mt: 3, mb: 2 }}>
                    <Typography variant="h6" gutterBottom>
                        Free First Training Options
                    </Typography>
                    <FormGroup>
                        {trainingOptions.map((training) => (
                            <FormControlLabel
                                key={training}
                                control={
                                    <Checkbox
                                        checked={selectedTrainings.includes(training)}
                                        onChange={() => handleTrainingChange(training)}
                                        name={training}
                                    />
                                }
                                label={training}
                            />
                        ))}
                    </FormGroup>
                </Box>

                <Divider sx={{ my: 3 }} />

                {/* Trainers Section */}
                <Typography variant="h6" sx={{ mb: 2, color: 'primary.main' }}>
                    Trainers Management
                </Typography>

                <Typography variant="subtitle1" sx={{ mt: 2 }}>Search Trainers</Typography>
                <TextField label="Search" fullWidth value={trainerSearch} onChange={handleTrainerSearch} />
                <List>
                    {searchResults.map((trainer) => (
                        <ListItem key={trainer.id} button onClick={() => handleSelectTrainer(trainer)}>
                            {trainer.fullName}
                        </ListItem>
                    ))}
                </List>

                <Typography variant="subtitle1" sx={{ mt: 2 }}>Selected Trainers</Typography>
                <List>
                    {selectedTrainers.map((trainer) => (
                        <ListItem key={trainer.trainerId} secondaryAction={
                            <IconButton edge="end" onClick={() => handleRemoveTrainer(trainer.trainerId)}>
                                <DeleteIcon />
                            </IconButton>
                        }>
                            {trainer.fullName}
                        </ListItem>
                    ))}
                </List>

                <FormHelperText sx={{ mb: 2 }}>
                    * Required fields
                </FormHelperText>

                <Button variant="contained" color="primary" onClick={handleSave} sx={{ mt: 2 }}>Save</Button>
                <Button variant="outlined" color="inherit" onClick={onCancel} sx={{ mt: 2, ml: 2 }}>Cancel</Button>
            </CardContent>
        </Card>
    );
}